import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './WorldMapGuide.css';
import worldMap from './assets/world_map.png';
import region1 from './assets/region1.png'; // Zone 1: USA, Canada, and Mexico
import region2 from './assets/region2.png'; // Zone 2: Europe
import region3 from './assets/region3.png'; // Zone 3: Asia, Oceania, and Australia
import region4 from './assets/region4.png'; // Zone 4: Africa and the Middle East
import region5 from './assets/region5.png'; // Zone 5: South America
import region6 from './assets/region6.png'; // Zone 6: Antarctica
import region7 from './assets/region7.png'; // Zone 7: North Pole
import region8 from './assets/region8.png'; // Zone 8: The Arcane Vale
import region9 from './assets/region9.png'; // Zone 9: Intergalactic Starbase
import region10 from './assets/region10.png'; // Zone 10: The Temporal Shift

const WorldMapGuide = () => {
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, content: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRegions, setExpandedRegions] = useState({}); // Track which regions' locations are expanded

  useEffect(() => {
    const fetchZones = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/regions`, {
          headers: { 'player-id': localStorage.getItem('playerId') || '' }
        });
        console.log('Fetched zones:', res.data); // Debug log to check displayName values
        setZones(res.data);
      } catch (err) {
        setError('Failed to load zones. Please try again later.');
        console.error('Error fetching zones:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchZones();
  }, []);

  // Map regionId (Zone ID) to corresponding image
  const zoneImages = {
    1: region1,
    2: region2,
    3: region3,
    4: region4,
    5: region5,
    6: region6,
    7: region7,
    8: region8,
    9: region9,
    10: region10,
  };

  // Guide messages for locked zones
  const lockedZoneGuides = {
    8: 'The Arcane Vale Guide',
    9: 'The Intergalactic Starbase Guide',
    10: 'The Temporal Shift Guide'
  };

  const handleZoneSelect = (e) => {
    const zoneId = parseInt(e.target.value, 10);
    const zone = zones.find(z => z.regionId === zoneId);
    if (selectedZone && selectedZone.regionId === zoneId) {
      setSelectedZone(null); // Reset to default view
      setExpandedRegions({}); // Reset expanded regions when deselecting
    } else {
      setSelectedZone(zone);
      setExpandedRegions({}); // Reset expanded regions when selecting a new zone
      if (zone && !zone.isAccessible) {
        setTooltip({
          show: true,
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
          content: `Zone ${zone.regionId} is locked. Please view ${lockedZoneGuides[zone.regionId]} to learn more.`
        });
      } else {
        setTooltip({ show: false });
      }
    }
    console.log('Selected Zone:', zone); // Debug log to check selected zone data
  };

  const toggleRegionLocations = (regionId) => {
    setExpandedRegions(prev => ({
      ...prev,
      [regionId]: !prev[regionId]
    }));
  };

  if (loading) return <p style={{ textAlign: 'center', color: '#333' }}>Loading zones...</p>;
  if (error) return <p style={{ textAlign: 'center', color: '#333' }}>{error}</p>;

  // Use the selected zone's image if accessible, otherwise default to worldMap
  const currentMap = selectedZone && selectedZone.isAccessible ? zoneImages[selectedZone.regionId] || worldMap : worldMap;

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '60px 20px', textAlign: 'left' }}>
      <h1 style={{ color: '#1E88E5', fontSize: '2em', marginBottom: '20px', textAlign: 'center' }}>
        World Map
      </h1>
      <p style={{ color: '#333', fontSize: '16px', marginBottom: '20px', textAlign: 'center' }}>
        Explore the world of the Dog Fanciers Guild! Select a Zone below to learn about the regions there.
      </p>
      <div style={{ position: 'relative', textAlign: 'center', marginBottom: '40px' }}>
        <img
          src={currentMap}
          alt="World Map"
          style={{ maxWidth: '80%', height: 'auto', display: 'block', margin: '0 auto', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}
        />
        <select
          onChange={handleZoneSelect}
          value={selectedZone ? selectedZone.regionId : ''}
          style={{
            display: 'block',
            margin: '10px auto',
            padding: '8px',
            width: '350px',
            backgroundColor: '#fff',
            border: '2px solid #1E88E5',
            borderRadius: '5px',
            fontSize: '1em',
            cursor: 'pointer',
            transition: 'border-color 0.3s'
          }}
        >
          <option value="">Select a Zone</option>
          {zones.map(zone => (
            <option key={zone.regionId} value={zone.regionId}>
              Zone {zone.regionId}
            </option>
          ))}
        </select>
        {tooltip.show && (
          <div
            style={{
              position: 'absolute',
              left: tooltip.x,
              top: tooltip.y,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '2px solid #1E88E5',
              borderRadius: '5px',
              padding: '10px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              fontSize: '1em',
              color: '#333',
              zIndex: 1000
            }}
          >
            {tooltip.content}
          </div>
        )}
      </div>

      {selectedZone && (
        <div
          style={{
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            marginBottom: '15px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
            border: '1px solid #E6F0FA'
          }}
        >
          <div
            style={{
              padding: '15px',
              backgroundColor: '#E6F0FA',
              borderRadius: '8px 8px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <h2 style={{ color: '#4CAF50', fontSize: '1.5em', margin: 0 }}>
              {`Zone ${selectedZone.regionId} - ${selectedZone.displayName || selectedZone.name || 'Unknown Region'}`}
            </h2>
          </div>
          <div style={{ padding: '15px' }}>
            <h3 style={{ color: '#1E88E5', fontSize: '1.2em', marginBottom: '10px' }}>Regions in this Zone</h3>
            {selectedZone.regions && selectedZone.regions.length > 0 ? (
              selectedZone.regions.map((region) => (
                <div
                  key={region._id}
                  style={{
                    padding: '10px',
                    backgroundColor: '#fff',
                    borderRadius: '5px',
                    marginBottom: '10px',
                    border: '1px solid #E6F0FA'
                  }}
                >
                  <h4 style={{ color: '#333', fontSize: '1.1em', margin: '0 0 5px' }}>{region.name}</h4>
                  <div
                    style={{
                      padding: '10px',
                      cursor: 'pointer',
                      backgroundColor: expandedRegions[region._id] ? '#E6F0FA' : '#fff',
                      borderRadius: '5px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: '10px'
                    }}
                    onClick={() => toggleRegionLocations(region._id)}
                  >
                    <h5 style={{ color: '#1E88E5', fontSize: '1.1em', margin: '0' }}>Locations</h5>
                    <span style={{ color: '#1E88E5', fontSize: '1.2em' }}>
                      {expandedRegions[region._id] ? '−' : '+'}
                    </span>
                  </div>
                  {expandedRegions[region._id] && (
                    <div style={{ padding: '10px' }}>
{region.locations && region.locations.length > 0 ? (
  region.locations.map((loc) => {
    // Normalize background so we always have an array
    const bgArray = Array.isArray(loc.background)
      ? loc.background
      : loc.background
      ? [loc.background]
      : [];

    return (
      <div
        key={loc.name}
        style={{
          padding: '15px',
          backgroundColor: '#fff',
          borderRadius: '8px',
          marginBottom: '15px',
          border: '1px solid #E6F0FA',
          boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
        }}
      >
        <h6 style={{ color: '#333', fontSize: '1.1em', margin: '0 0 12px' }}>
          {loc.name}
        </h6>

        {/* 300×300 MAIN PREVIEW (first background) */}
        {bgArray.length > 0 ? (
          <div style={{ textAlign: 'center', marginBottom: '15px' }}>
            <img
              src={bgArray[0]}
              alt={`${loc.name} background`}
              style={{
                width: '300px',
                height: '300px',
                objectFit: 'cover',
                borderRadius: '10px',
                border: '2px solid #1E88E5',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                cursor: 'pointer'
              }}
              onClick={() => window.open(bgArray[0], '_blank')}
              title="Click to view full-size"
            />
            <p style={{ fontSize: '0.85em', color: '#666', marginTop: '6px' }}>
              Background Preview — click to enlarge
            </p>
          </div>
        ) : (
          <p style={{ color: '#999', fontStyle: 'italic', textAlign: 'center', marginBottom: '15px' }}>
            No background image yet
          </p>
        )}

        {/* Extra backgrounds as small thumbnails (if any) */}
        {bgArray.length > 1 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', marginBottom: '15px' }}>
            {bgArray.slice(1).map((url, idx) => (
              <img
                key={idx}
                src={url}
                alt={`${loc.name} extra background ${idx + 2}`}
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'cover',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  cursor: 'pointer'
                }}
                onClick={() => window.open(url, '_blank')}
              />
            ))}
          </div>
        )}

        {/* Rest of location info */}
        <p style={{ color: '#333', fontSize: '14px', margin: '8px 0' }}>
          <strong>ACE Animals:</strong> {loc.aceAnimals ? loc.aceAnimals.join(', ') : 'None'}
        </p>
        <p style={{ color: '#333', fontSize: '14px', margin: '8px 0' }}>
          <strong>Storyline:</strong> {loc.storyline || 'TBD'}
        </p>
        <p style={{ color: '#333', fontSize: '14px', margin: '8px 0' }}>
          <strong>Tasks:</strong> {loc.tasks ? loc.tasks.join(', ') : 'TBD'}
        </p>
      </div>
    );
  })
) : (
  <p style={{ color: '#333', fontStyle: 'italic' }}>No locations available</p>
)}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p style={{ color: '#333', fontSize: '14px', margin: '5px 0', fontStyle: 'italic' }}>
                No regions available in this zone
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorldMapGuide;